import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Popconfirm, Space, Checkbox, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config/api';

const { Option } = Select;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [editingUser, setEditingUser] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [profile, setProfile] = useState(null);

    // ระบบกำหนดสิทธิ์ (RBAC)
    const navigate = useNavigate();
    const [accessUser, setAccessUser] = useState(null);
    const [accessOpen, setAccessOpen] = useState(false);
    const [allRoles, setAllRoles] = useState([]);
    const [permGroups, setPermGroups] = useState({});
    const [selectedRoleIds, setSelectedRoleIds] = useState([]);
    const [allowPerms, setAllowPerms] = useState([]);
    const [denyPerms, setDenyPerms] = useState([]);
    const [savingAccess, setSavingAccess] = useState(false);

    const canManageAccess = () => {
        if (profile?.level === 'ภาค') return true;
        return (profile?.permissions || []).includes('user.assign_permissions');
    };


    const fetchUsers = async () => {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE_URL}/api/users`, {
            headers: { Authorization: `Bearer ${token}` },
        });

        setUsers(res.data);
    };

    const fetchProfile = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/api/users/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = { ...res.data, address: { province: res.data.province, district: res.data.district, subdistrict: res.data.subdistrict } };
            //console.log('Me:', data);
            setProfile(data);
            form.setFieldsValue(data);
        } catch (err) {
            message.error('ไม่สามารถโหลดข้อมูลโปรไฟล์');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProfile();
        fetchUsers();
    }, []);

    const openAddModal = () => {
        setEditingUser(null);
        form.resetFields();
        setIsModalOpen(true);
    };

    const openEditModal = (user) => {
        setEditingUser(user);
        form.setFieldsValue(user);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_BASE_URL}/api/users/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        message.success('ลบผู้ใช้เรียบร้อยแล้ว');
        fetchUsers();
    };

    const handleFinish = async () => {
        const values = await form.validateFields();
        const token = localStorage.getItem('token');
        try {
            if (editingUser) {
                await axios.put(`${API_BASE_URL}/api/users/${editingUser.id}`, values, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                message.success('แก้ไขผู้ใช้เรียบร้อยแล้ว');
            } else {
                await axios.post(`${API_BASE_URL}/api/users`, values, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                message.success('เพิ่มผู้ใช้เรียบร้อยแล้ว');
            }
            setIsModalOpen(false);
            fetchUsers();
        } catch (err) {
            message.error('เกิดข้อผิดพลาด');
        }
    };

    // ===== ระบบกำหนดสิทธิ์ (RBAC) =====
    const openAccessModal = async (user) => {
        setAccessUser(user);
        setAccessOpen(true);
        try {
            const [rolesRes, permRes, accessRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/roles`, { headers: authHeaders() }),
                axios.get(`${API_BASE_URL}/api/roles/permissions`, { headers: authHeaders() }),
                axios.get(`${API_BASE_URL}/api/roles/user/${user.id}`, { headers: authHeaders() }),
            ]);
            setAllRoles(rolesRes.data?.roles || []);
            setPermGroups(permRes.data?.groups || {});
            setSelectedRoleIds(accessRes.data?.roles || []);
            const overrides = accessRes.data?.overrides || [];
            setAllowPerms(overrides.filter((o) => o.allow === 1).map((o) => o.permission));
            setDenyPerms(overrides.filter((o) => o.allow === 0).map((o) => o.permission));
        } catch {
            message.error('ไม่สามารถโหลดข้อมูลสิทธิ์ได้');
        }
    };

    const saveAccess = async () => {
        setSavingAccess(true);
        try {
            await axios.put(`${API_BASE_URL}/api/roles/user/${accessUser.id}`, { roleIds: selectedRoleIds }, { headers: authHeaders() });
            const items = [
                ...allowPerms.map((p) => ({ permission: p, allow: 1 })),
                ...denyPerms.map((p) => ({ permission: p, allow: 0 })),
            ];
            await axios.put(`${API_BASE_URL}/api/roles/user/${accessUser.id}/permissions`, { permissions: items }, { headers: authHeaders() });
            message.success('บันทึกสิทธิ์เรียบร้อย');
            setAccessOpen(false);
        } catch (err) {
            message.error(err.response?.data?.message || 'ไม่สามารถบันทึกสิทธิ์ได้');
        } finally {
            setSavingAccess(false);
        }
    };

    const columns = [
        { title: 'ชื่อผู้ใช้', dataIndex: 'username' },
        { title: 'ชื่อจริง', dataIndex: 'name' },
        { title: 'ระดับสิทธิ์', dataIndex: 'level' },
        {
            title: 'การจัดการ',
            render: (_, record) => (
                <>
                    {record.username === 'admin' ? null : (
                        <>
                            {profile?.level === 'ภาค' && (
                                <>
                                    <Button type="link" onClick={() => openEditModal(record)}>แก้ไข</Button>
                                    {canManageAccess() && (
                                        <Button type="link" onClick={() => openAccessModal(record)}>กำหนดสิทธิ์</Button>
                                    )}
                                    <Popconfirm
                                        title="ยืนยันการลบผู้ใช้นี้?"
                                        onConfirm={() => handleDelete(record.id)}
                                    >
                                        <Button type="link" danger>ลบ</Button>
                                    </Popconfirm>
                                </>
                            )}

                        </>
                    )}

                </>
            ),
        },
    ];

    return (
        <>
            <h2>จัดการผู้ใช้</h2>

            {profile?.level === 'ภาค' && (
                <>
                    <Space style={{ marginBottom: 16 }}>
                        <Button type="primary" onClick={openAddModal}>
                            เพิ่มผู้ใช้
                        </Button>
                        {canManageAccess() && (
                            <Button onClick={() => navigate('/roles')}>จัดการบทบาท/สิทธิ์</Button>
                        )}
                    </Space>
                    <Table columns={columns} dataSource={users} rowKey="id" scroll={{ x: 'max-content' }} />
                </>
            )}


            <Modal
                title={editingUser ? 'แก้ไขผู้ใช้' : 'เพิ่มผู้ใช้'}
                open={isModalOpen}
                onOk={handleFinish}
                okText='บันทึก'
                cancelText='ยกเลิก'
                onCancel={() => setIsModalOpen(false)}
                destroyOnClose
                width="min(560px, 96vw)"
            >
                <Form form={form} layout="vertical" onFinish={handleFinish}>
                    <Form.Item name="username" label="ชื่อผู้ใช้" rules={[{ required: true }]}>
                        <Input readOnly={editingUser} />
                    </Form.Item>
                    {!editingUser && (
                        <>
                            <Form.Item name="password" label="รหัสผ่าน" rules={[{ required: true }]}>
                                <Input.Password />
                            </Form.Item>
                        </>
                    )}
                    <Form.Item name="name" label="ชื่อ-นามสกุล" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="role" label="ประเภทผู้ใช้" rules={[{ required: true }]}>
                        <Select>
                            <Option value="admin">ผู้ดูแลระบบ</Option>
                            <Option value="editor">นักเขียน</Option>
                            <Option value="viewer">ดูอย่างเดียว</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="level" label="ระดับสิทธิ์" rules={[{ required: true }]}>
                        <Select>
                            <Option value="ตำบล">เจ้าคณะตำบล</Option>
                            <Option value="อำเภอ">เจ้าคณะอำเภอ</Option>
                            <Option value="จังหวัด">เจ้าคณะจังหวัด</Option>
                            <Option value="ภาค">เจ้าคณะภาค</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="province" label="จังหวัด">
                        <Input />
                    </Form.Item>
                    <Form.Item name="district" label="อำเภอ">
                        <Input />
                    </Form.Item>
                    <Form.Item name="subdistrict" label="ตำบล">
                        <Input />
                    </Form.Item>
                    {/* <Form.Item>
                        <Button type="primary" htmlType="submit">
                            บันทึก
                        </Button>
                    </Form.Item> */}
                </Form>
            </Modal>

            {/* Modal กำหนดสิทธิ์ (RBAC) */}
            <Modal
                title={`กำหนดสิทธิ์: ${accessUser?.name || ''}`}
                open={accessOpen}
                onOk={saveAccess}
                okText="บันทึกสิทธิ์"
                confirmLoading={savingAccess}
                cancelText="ปิด"
                onCancel={() => setAccessOpen(false)}
                width="min(760px, 96vw)"
                destroyOnClose
            >
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>บทบาท (Roles)</div>
                    <Checkbox.Group
                        options={allRoles.map((r) => ({ label: r.name, value: r.id }))}
                        value={selectedRoleIds}
                        onChange={setSelectedRoleIds}
                    />
                </div>
                <div style={{ borderTop: '1px solid #eee', paddingTop: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>สิทธิ์เฉพาะรายบุคคล (Override)</div>
                    <div style={{ color: '#888', fontSize: 12, marginBottom: 10 }}>
                        Allow = อนุญาตเพิ่มเหนือกว่าบทบาท &nbsp;·&nbsp; Deny = ห้ามแม้บทบาทจะให้สิทธิ์
                    </div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 280 }}>
                            <Tag color="green">Allow</Tag>
                            <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8, padding: 10, marginTop: 6 }}>
                                {Object.entries(permGroups).map(([group, perms]) => (
                                    <div key={group} style={{ marginBottom: 8 }}>
                                        <div style={{ fontWeight: 600, fontSize: 12, color: '#43290f' }}>{group}</div>
                                        <Checkbox.Group
                                            options={perms.map((p) => ({ label: p.name, value: p.code }))}
                                            value={allowPerms}
                                            onChange={setAllowPerms}
                                            style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div style={{ flex: 1, minWidth: 280 }}>
                            <Tag color="red">Deny</Tag>
                            <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8, padding: 10, marginTop: 6 }}>
                                {Object.entries(permGroups).map(([group, perms]) => (
                                    <div key={group} style={{ marginBottom: 8 }}>
                                        <div style={{ fontWeight: 600, fontSize: 12, color: '#43290f' }}>{group}</div>
                                        <Checkbox.Group
                                            options={perms.map((p) => ({ label: p.name, value: p.code }))}
                                            value={denyPerms}
                                            onChange={setDenyPerms}
                                            style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default UserManagement;
